/**
 * Dashboard page with KPIs and overview.
 */

import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { SparkLine } from '@/components/charts/spark-line';
import { LoadingSpinner } from '@/components/ui/loading';
import { usePlacements } from '@/hooks/usePlacements';
import { useTopSparks } from '@/hooks/useAnalytics';
import { useUsage } from '@/hooks/useAnalytics';
import { Link } from 'react-router-dom';
import { TrendingUp, Users, MousePointer, Link as LinkIcon } from 'lucide-react';
import './dashboard.scss';

export function Dashboard() {
  const { data: placements, isLoading: placementsLoading } = usePlacements();
  const { data: topSparks, isLoading: sparksLoading } = useTopSparks({ limit: 5 });
  const { data: usage, isLoading: usageLoading } = useUsage();

  const isLoading = placementsLoading || sparksLoading || usageLoading;

  // Mock sparkline data - in real app, fetch time series
  const mockSparklineData = Array.from({ length: 7 }, (_, i) => ({
    t: new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000).toISOString(),
    value: Math.floor(Math.random() * 100),
  }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <h1 className="h1">Dashboard</h1>
        <p className="subtitle">Overview of your link tracking performance</p>
      </div>

      {/* KPI Cards */}
      <div className="dashboard__kpi-grid">
        <Card>
          <div className="dashboard__kpi-card">
            <div>
              <p className="small">Total Clicks</p>
              <p className="h2" style={{ marginTop: 'var(--of-space-2)' }}>
                {usage?.usage.clicks.toLocaleString() || '0'}
              </p>
            </div>
            <div className="dashboard__kpi-card-icon dashboard__kpi-card-icon--primary">
              <MousePointer />
            </div>
          </div>
          <div className="dashboard__kpi-card-sparkline">
            <SparkLine data={mockSparklineData} />
          </div>
        </Card>

        <Card>
          <div className="dashboard__kpi-card">
            <div>
              <p className="small">Unique Visitors</p>
              <p className="h2" style={{ marginTop: 'var(--of-space-2)' }}>
                {usage?.usage.clicks.toLocaleString() || '0'}
              </p>
            </div>
            <div className="dashboard__kpi-card-icon dashboard__kpi-card-icon--success">
              <Users />
            </div>
          </div>
          <div className="dashboard__kpi-card-sparkline">
            <SparkLine data={mockSparklineData} color="var(--of-success)" />
          </div>
        </Card>

        <Card>
          <div className="dashboard__kpi-card">
            <div>
              <p className="small">Active Placements</p>
              <p className="h2" style={{ marginTop: 'var(--of-space-2)' }}>
                {placements?.placements.length || 0}
              </p>
            </div>
            <div className="dashboard__kpi-card-icon dashboard__kpi-card-icon--info">
              <LinkIcon />
            </div>
          </div>
          <div className="dashboard__kpi-card-sparkline">
            <SparkLine data={mockSparklineData} color="var(--of-flow-blue)" />
          </div>
        </Card>

        <Card>
          <div className="dashboard__kpi-card">
            <div>
              <p className="small">Top Sparks</p>
              <p className="h2" style={{ marginTop: 'var(--of-space-2)' }}>
                {topSparks?.sparks.length || 0}
              </p>
            </div>
            <div className="dashboard__kpi-card-icon dashboard__kpi-card-icon--highlight">
              <TrendingUp />
            </div>
          </div>
          <div className="dashboard__kpi-card-sparkline">
            <SparkLine data={mockSparklineData} color="var(--of-highlight)" />
          </div>
        </Card>
      </div>

      {/* Top Sparks */}
      <Card>
        <h2 className="h3" style={{ marginBottom: 'var(--of-space-4)' }}>Top Sparks</h2>
        {topSparks && topSparks.sparks.length > 0 ? (
          <div className="dashboard__sparks-list">
            {topSparks.sparks.map((spark, index) => (
              <div key={index} className="dashboard__spark-item">
                <div className="flex items-center">
                  <div className="dashboard__spark-item-rank">
                    {index + 1}
                  </div>
                  <div className="dashboard__spark-item-info">
                    <p className="dashboard__spark-item-info-host">{spark.ref_host}</p>
                    <p className="dashboard__spark-item-info-clicks">{spark.clicks} clicks</p>
                  </div>
                </div>
                <div className="dashboard__spark-item-score">
                  <p className="dashboard__spark-item-score-value">{spark.spark_score.toFixed(2)}</p>
                  <p className="dashboard__spark-item-score-label">Spark Score</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="of-text-muted">No sparks data available</p>
        )}
      </Card>

      {/* Usage Limits */}
      {usage && (
        <Card>
          <h2 className="h3" style={{ marginBottom: 'var(--of-space-4)' }}>Usage & Limits</h2>
          <div className="dashboard__usage">
            <div className="dashboard__usage-bar">
              <div className="dashboard__usage-bar-label">
                <span className="dashboard__usage-bar-label-text">Domains</span>
                <span className="dashboard__usage-bar-label-value">
                  {usage.usage.domains} / {usage.limits.domains}
                </span>
              </div>
              <div className="dashboard__usage-bar-track">
                <div
                  className="dashboard__usage-bar-fill"
                  style={{
                    width: `${Math.min((usage.usage.domains / usage.limits.domains) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>
            <div className="dashboard__usage-bar">
              <div className="dashboard__usage-bar-label">
                <span className="dashboard__usage-bar-label-text">Links</span>
                <span className="dashboard__usage-bar-label-value">
                  {usage.usage.links} / {usage.limits.links}
                </span>
              </div>
              <div className="dashboard__usage-bar-track">
                <div
                  className="dashboard__usage-bar-fill"
                  style={{
                    width: `${Math.min((usage.usage.links / usage.limits.links) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>
            <div className="dashboard__usage-bar">
              <div className="dashboard__usage-bar-label">
                <span className="dashboard__usage-bar-label-text">Clicks</span>
                <span className="dashboard__usage-bar-label-value">
                  {usage.usage.clicks.toLocaleString()} / {usage.limits.clicks.toLocaleString()}
                </span>
              </div>
              <div className="dashboard__usage-bar-track">
                <div
                  className="dashboard__usage-bar-fill"
                  style={{
                    width: `${Math.min((usage.usage.clicks / usage.limits.clicks) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

